/**
 * Configuracion de idiomas de la interfaz.
 *
 * Un solo idioma activo cada vez, con los otros dos cargados y ocultos, tal
 * como se acordo. El idioma elegido se guarda para que no se pierda entre
 * sesiones ni al cerrar la aplicacion.
 *
 * Efectos que van con el cambio de idioma y que no hay que olvidar:
 *  - el atributo lang del documento, del que depende un lector de pantalla
 *    para pronunciar bien,
 *  - el atributo dir, preparado para poder anadir un idioma de derecha a
 *    izquierda mas adelante sin rehacer el arranque,
 *  - las peticiones a la API, que devuelven el contenido en el idioma activo.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import DetectorIdioma from 'i18next-browser-languagedetector';
import { ETIQUETA_BCP47, IDIOMAS, IDIOMA_POR_DEFECTO, type Idioma, esIdioma } from '@recorregut/shared';
import ca from './ca.json';
import es from './es.json';
import en from './en.json';

export const CLAVE_ALMACEN_IDIOMA = 'recorregut.idioma';

/** Idiomas que se escriben de derecha a izquierda. Ninguno todavia. */
const IDIOMAS_RTL: readonly string[] = [];

export function direccionDeIdioma(idioma: Idioma): 'ltr' | 'rtl' {
  return IDIOMAS_RTL.includes(idioma) ? 'rtl' : 'ltr';
}

export function aplicarIdiomaAlDocumento(idioma: Idioma): void {
  const raiz = document.documentElement;
  raiz.setAttribute('lang', idioma);
  raiz.setAttribute('dir', direccionDeIdioma(idioma));
}

await i18n
  .use(DetectorIdioma)
  .use(initReactI18next)
  .init({
    resources: {
      ca: { translation: ca },
      es: { translation: es },
      en: { translation: en },
    },
    supportedLngs: [...IDIOMAS],
    fallbackLng: IDIOMA_POR_DEFECTO,
    // Sin region: el contenido esta cargado por idioma, no por variante.
    load: 'languageOnly',
    nonExplicitSupportedLngs: true,
    detection: {
      /*
       * Solo la preferencia guardada. NO se mira el idioma del navegador.
       *
       * Esto es una gimcana de la Federacio d'Hostafrancs y la lengua de la
       * casa es el catalan: quien llega sin haber elegido nada tiene que ver la
       * aplicacion en catalan, aunque traiga el movil en castellano o en
       * ingles, que es lo que pasa con la mayoria de los moviles del barrio.
       * Con `navigator` en la lista, el idioma del dispositivo mandaba sobre el
       * del evento y el catalan casi no aparecia.
       *
       * No se pierde nada: el selector esta siempre visible en la cabecera y la
       * eleccion se guarda, asi que cambiarlo cuesta un toque y se recuerda.
       */
      order: ['localStorage'],
      lookupLocalStorage: CLAVE_ALMACEN_IDIOMA,
      caches: ['localStorage'],
    },
    interpolation: {
      // React ya escapa por su cuenta; hacerlo dos veces rompe los apostrofes,
      // que en catalan salen constantemente.
      escapeValue: false,
    },
    returnNull: false,
  });

/** Idioma activo, siempre uno de los soportados. */
export function idiomaActivo(): Idioma {
  const actual = i18n.resolvedLanguage ?? i18n.language;
  return esIdioma(actual) ? actual : IDIOMA_POR_DEFECTO;
}

export function etiquetaFormato(idioma: Idioma = idiomaActivo()): string {
  return ETIQUETA_BCP47[idioma];
}

export async function cambiarIdioma(idioma: Idioma): Promise<void> {
  await i18n.changeLanguage(idioma);
  aplicarIdiomaAlDocumento(idioma);
}

aplicarIdiomaAlDocumento(idiomaActivo());

export default i18n;
